import { create } from 'zustand';
import { 
  DashboardProductResultDto,
  DashboardMainTopDto,
  DashboardKpiHistoryDto,
  DashboardProductionDetailDto,
  DashboardKpiSendDetailDto,
  DashboardProcessMonitoringDto,
  DashboardProcessInstructionDto,
  DashboardNoticeDto
} from './Dto_Home';
import {
  getMonthlyProduction,
  getItemGroupProduction,
  getMainTop,
  getKpiHistory,
  getProductionDetails,
  getKpiSendDetail,
  getProcessMonitoring,
  getProcessInstructions,
  getDashboardNoticeList,
  getDashboardNoticeOne,
} from './Api_Home';
import { formatLocalDate } from '../../../utils/date/dateUtils';

// 화면별로 필요한 데이터만 선택적으로 조회하기 위한 옵션
// - includeATypeData: A화면(Default) 전용 (KPI 전송내역)
// - includeBTypeData: B화면 전용 (공정 실적 모니터링 / 공정 지시내역)
// - includeCTypeData: C화면 전용 (공지사항 목록)
export interface DashboardFetchOptions {
  includeATypeData?: boolean;
  includeBTypeData?: boolean;
  includeCTypeData?: boolean;
}

// 대시보드 상태 인터페이스
interface DashboardState {
  // 로딩 및 상태 관리
  isLoading: boolean;
  hasLoadedOnce: boolean;
  dataSource: 'api' | 'empty';
  error: string | null;
  
  // 날짜 범위
  startDate: Date;
  endDate: Date;
  
  // 데이터 상태
  monthlyProductionData: DashboardProductResultDto[];      // 브랜드별 생산실적 현황
  itemGroupData: DashboardProductResultDto[];             // 월별 불량현황
  batchDefectData: DashboardProductResultDto[];           // 품목그룹별 생산실적 현황
  kpiHistoryData: DashboardKpiHistoryDto[];               // KPI 전송현황
  productionDetailData: DashboardProductionDetailDto[];   // 상세 생산실적
  kpiSendDetailData: DashboardKpiSendDetailDto[];         // KPI 전송내역
  processMonitoringData: DashboardProcessMonitoringDto[]; // 공정 실적 모니터링
  processInstructionData: DashboardProcessInstructionDto[]; // 공정 지시내역
  mainTopData: DashboardMainTopDto | null;                // 요약 카드
  noticeListData: DashboardNoticeDto[];                  // 공지사항 목록 (타입C)
  selectedNoticeData: DashboardNoticeDto | null;         // 선택한 공지사항 상세 (타입C)
  
  // 액션들
  setLoading: (loading: boolean) => void;
  setDataSource: (source: 'api' | 'empty') => void;
  setError: (error: string | null) => void;
  setDateRange: (startDate: Date, endDate: Date) => void;
  
  // 데이터 설정 액션들
  setMonthlyProductionData: (data: DashboardProductResultDto[]) => void;
  setItemGroupData: (data: DashboardProductResultDto[]) => void;
  setBatchDefectData: (data: DashboardProductResultDto[]) => void;
  setKpiHistoryData: (data: DashboardKpiHistoryDto[]) => void;
  setProductionDetailData: (data: DashboardProductionDetailDto[]) => void;
  setKpiSendDetailData: (data: DashboardKpiSendDetailDto[]) => void;
  setProcessMonitoringData: (data: DashboardProcessMonitoringDto[]) => void;
  setProcessInstructionData: (data: DashboardProcessInstructionDto[]) => void;
  setMainTopData: (data: DashboardMainTopDto | null) => void;
  setNoticeListData: (data: DashboardNoticeDto[]) => void;
  setSelectedNoticeData: (data: DashboardNoticeDto | null) => void;
  fetchNoticeOne: (noticeNo: string) => Promise<void>;
  
  // 복합 액션들
  fetchDashboardData: (compCd?: string, options?: DashboardFetchOptions) => Promise<void>;
  clearAllData: () => void;
  refreshData: (compCd?: string, options?: DashboardFetchOptions) => Promise<void>;
  updateDateRangeAndFetch: (startDate: Date, endDate: Date, compCd?: string, options?: DashboardFetchOptions) => Promise<void>;
}

// Zustand store 생성
export const useDashboardStore = create<DashboardState>((set, get) => ({
  // 초기 상태
  isLoading: false,
  hasLoadedOnce: false,
  dataSource: 'api',
  error: null,
  
  startDate: new Date(new Date().getFullYear(), 0, 1),
  endDate: new Date(),
  
  monthlyProductionData: [],
  itemGroupData: [],
  batchDefectData: [],
  kpiHistoryData: [],
  productionDetailData: [],
  kpiSendDetailData: [],
  processMonitoringData: [],
  processInstructionData: [],
  mainTopData: null,
  noticeListData: [],
  selectedNoticeData: null,
  
  // 기본 액션들
  setLoading: (loading) => set({ isLoading: loading }),
  setDataSource: (source) => set({ dataSource: source }),
  setError: (error) => set({ error }),
  setDateRange: (startDate, endDate) => set({ startDate, endDate }),
  
  // 데이터 설정 액션들
  setMonthlyProductionData: (data) => set({ monthlyProductionData: data }),
  setItemGroupData: (data) => set({ itemGroupData: data }),
  setBatchDefectData: (data) => set({ batchDefectData: data }),
  setKpiHistoryData: (data) => set({ kpiHistoryData: data }),
  setProductionDetailData: (data) => set({ productionDetailData: data }),
  setKpiSendDetailData: (data) => set({ kpiSendDetailData: data }),
  setProcessMonitoringData: (data) => set({ processMonitoringData: data }),
  setProcessInstructionData: (data) => set({ processInstructionData: data }),
  setMainTopData: (data) => set({ mainTopData: data }),
  setNoticeListData: (data) => set({ noticeListData: data }),
  setSelectedNoticeData: (data) => set({ selectedNoticeData: data }),

  // 공지사항 상세 조회 (타입C)
  fetchNoticeOne: async (noticeNo: string) => {
    try {
      const response = await getDashboardNoticeOne(noticeNo);
      set({ selectedNoticeData: response.data || null });
    } catch {
      set({ selectedNoticeData: null });
    }
  },
  
  // 모든 대시보드 데이터 조회
  fetchDashboardData: async (compCd?: string, options?: DashboardFetchOptions) => {
    const { startDate, endDate } = get();
    const includeATypeData = options?.includeATypeData ?? false;
    const includeBTypeData = options?.includeBTypeData ?? false;
    const includeCTypeData = options?.includeCTypeData ?? false;
    let loadedSuccessfully = false;

    set({ isLoading: true, error: null });

    try {
      const [
        monthlyProd,
        itemGroup,
        kpiHistory,
        productionDetail,
        mainTop,
      ] = await Promise.all([
        getMonthlyProduction(startDate, endDate),      // 브랜드별 생산실적 현황
        getItemGroupProduction(startDate, endDate),    // 월별 불량현황 + 품목그룹별 생산실적 현황
        getKpiHistory(startDate, endDate),             // KPI 전송현황
        getProductionDetails(startDate, endDate),      // 상세 생산실적
        getMainTop(startDate, endDate),                // 요약 카드
      ]);

      // 화면별 전용 데이터는 필요할 때만 실제로 조회한다.
      const [kpiSendDetail, processMonitoring, processInstruction, notice] = await Promise.all([
        includeATypeData
          ? getKpiSendDetail(startDate, endDate)       // KPI 전송내역 (A화면 전용)
          : Promise.resolve({ data: [] as DashboardKpiSendDetailDto[] }),
        includeBTypeData
          ? getProcessMonitoring(compCd || '', formatLocalDate(new Date()))       // 공정 실적 모니터링 (B화면 전용)
          : Promise.resolve({ data: [] as DashboardProcessMonitoringDto[] }),
        includeBTypeData
          ? getProcessInstructions(compCd || '', formatLocalDate(startDate), formatLocalDate(endDate)) // 공정 지시내역 (B화면 전용)
          : Promise.resolve({ data: [] as DashboardProcessInstructionDto[] }),
        includeCTypeData
          ? getDashboardNoticeList(startDate, endDate) // 공지사항 목록 (C화면 전용)
          : Promise.resolve({ data: [] as DashboardNoticeDto[] }),
      ]);

      // console.log(' API 데이터 사용');
      set({
        dataSource: 'api',
        monthlyProductionData: monthlyProd.data || [],
        itemGroupData: itemGroup.data || [],
        batchDefectData: itemGroup.data || [], // itemGroup 데이터를 월별불량현황과 품목그룹별 차트 둘 다 사용
        kpiHistoryData: kpiHistory.data || [],
        productionDetailData: productionDetail.data || [],
        kpiSendDetailData: kpiSendDetail.data || [],
        processMonitoringData: processMonitoring.data || [],
        processInstructionData: processInstruction.data || [],
        noticeListData: notice.data || [],
        selectedNoticeData: null,
        mainTopData: mainTop.data,
        error: null
      });
      loadedSuccessfully = true;
      
    } catch (error: any) {
      // console.error('❌ API 호출 실패:', error);
      set({ 
        dataSource: 'empty',
        error: error.message ,
        monthlyProductionData: [],
        itemGroupData: [],
        batchDefectData: [],
        kpiHistoryData: [],
        productionDetailData: [],
        kpiSendDetailData: [],
        processMonitoringData: [],
        processInstructionData: [],
        noticeListData: [],
        selectedNoticeData: null,
        mainTopData: null
      });
    } finally {
      set((state) => ({
        isLoading: false,
        hasLoadedOnce: state.hasLoadedOnce || loadedSuccessfully,
      }));
    }
  },
  
  // 모든 데이터 초기화
  clearAllData: () => {
    set({
      monthlyProductionData: [],
      itemGroupData: [],
      batchDefectData: [],
      kpiHistoryData: [],
      productionDetailData: [],
      kpiSendDetailData: [],
      processMonitoringData: [],
      processInstructionData: [],
      noticeListData: [],
      selectedNoticeData: null,
      mainTopData: null,
      hasLoadedOnce: false,
      error: null
    });
  },
  
  // 데이터 새로고침
  refreshData: async (compCd?: string, options?: DashboardFetchOptions) => {
    const { fetchDashboardData } = get();
    await fetchDashboardData(compCd, options);
  },

  // 날짜 범위 변경 후 자동 데이터 조회
  updateDateRangeAndFetch: async (startDate: Date, endDate: Date, compCd?: string, options?: DashboardFetchOptions) => {
    set({ startDate, endDate });
    const { fetchDashboardData } = get();
    await fetchDashboardData(compCd, options);
  }
})); 
